import type {
    EntityPatch,
    HistoryDomainAdapter,
    HistoryEntitySnapshot,
    HistoryPatchApplyInput,
} from '..';
import type { DatumPointHistorySnapshot } from '../../reference';
import { DATUM_POINT_HISTORY_ENTITY_ID } from '../../reference';
import { isSerializablePlainData } from './SerializablePlainData';

//------ 基准点撤销重做 开始------
/**
 * 基准点历史 adapter ID。
 */
export const DATUM_POINT_HISTORY_ADAPTER_ID = 'history-domain:datum-point';

/**
 * 基准点历史运行时存储。
 */
export interface DatumPointHistoryStore {
    /**
     * 恢复基准点历史快照。
     *
     * @param snapshot 基准点历史快照。
     */
    readonly restoreDatumPointHistorySnapshot: (snapshot: DatumPointHistorySnapshot) => void;
}

/**
 * 判断对象是否为普通对象。
 *
 * @param value 待判断对象。
 * @returns 是否为普通对象。
 */
function isPlainObject(value: object): boolean {
    const prototype = Object.getPrototypeOf(value);

    return prototype === Object.prototype || prototype === null;
}

/**
 * 递归移除历史快照中的 `undefined` 字段。
 *
 * @param value 待归一化值。
 * @returns 已移除 `undefined` 的值。
 */
function omitUndefinedDeep(value: unknown): unknown {
    if (Array.isArray(value)) {
        return value
            .map((item) => omitUndefinedDeep(item))
            .filter((item) => item !== undefined);
    }

    if (!value || typeof value !== 'object') {
        return value;
    }

    if (!isPlainObject(value)) {
        return value;
    }

    return Object.fromEntries(
        Object.entries(value as Record<string, unknown>)
            .map(([key, item]) => [key, omitUndefinedDeep(item)] as const)
            .filter(([, item]) => item !== undefined),
    );
}

/**
 * 归一化基准点历史快照。
 *
 * @param snapshot 来源快照。
 * @returns 可序列化快照。
 */
function normalizeDatumPointHistorySnapshot(snapshot: DatumPointHistorySnapshot): DatumPointHistorySnapshot {
    return omitUndefinedDeep(snapshot) as DatumPointHistorySnapshot;
}

/**
 * 归一化基准点实体快照。
 *
 * @param snapshot 来源实体快照。
 * @returns 可序列化实体快照。
 */
function normalizeDatumPointEntitySnapshot(
    snapshot: HistoryEntitySnapshot<DatumPointHistorySnapshot>,
): HistoryEntitySnapshot<DatumPointHistorySnapshot> {
    if (!snapshot.exists || !snapshot.data) {
        return snapshot;
    }

    return {
        ...snapshot,
        data: normalizeDatumPointHistorySnapshot(snapshot.data),
    };
}

/**
 * 判断基准点快照是否为 plain data。
 *
 * @param snapshot 基准点快照。
 * @returns 是否为可序列化 plain data。
 */
export function isPlainDatumPointHistorySnapshot(snapshot: DatumPointHistorySnapshot): boolean {
    const normalizedSnapshot = normalizeDatumPointHistorySnapshot(snapshot);

    return typeof normalizedSnapshot.managerId === 'string'
        && Array.isArray(normalizedSnapshot.points)
        && Array.isArray(normalizedSnapshot.features)
        && isSerializablePlainData(normalizedSnapshot);
}

/**
 * 应用基准点历史快照。
 *
 * @param input 补丁应用输入。
 */
function applyDatumPointSnapshot(
    input: HistoryPatchApplyInput<DatumPointHistorySnapshot, DatumPointHistoryStore>,
): void {
    const snapshot = input.snapshot;

    if (!snapshot.exists) {
        input.context.restoreDatumPointHistorySnapshot({
            managerId: DATUM_POINT_HISTORY_ENTITY_ID,
            points: [],
            features: [],
        });
        return;
    }

    if (!snapshot.data) {
        throw new Error(`基准点 ${input.patch.entityId} 缺少快照数据。`);
    }

    const normalizedSnapshot = normalizeDatumPointHistorySnapshot(snapshot.data);
    if (!isPlainDatumPointHistorySnapshot(normalizedSnapshot)) {
        throw new Error(`基准点 ${input.patch.entityId} 的快照不是可序列化 plain data。`);
    }

    input.context.restoreDatumPointHistorySnapshot(normalizedSnapshot);
}

/**
 * 比较基准点历史快照。
 *
 * @param left 左侧快照。
 * @param right 右侧快照。
 * @returns 是否语义相等。
 */
function areDatumPointSnapshotsEqual(
    left: HistoryEntitySnapshot<DatumPointHistorySnapshot>,
    right: HistoryEntitySnapshot<DatumPointHistorySnapshot>,
): boolean {
    return JSON.stringify(left) === JSON.stringify(right);
}

/**
 * 创建基准点历史 adapter。
 *
 * @returns 基准点历史 adapter。
 */
export function createDatumPointHistoryAdapter(): HistoryDomainAdapter<
    DatumPointHistorySnapshot,
    DatumPointHistoryStore
> {
    return {
        adapterId: DATUM_POINT_HISTORY_ADAPTER_ID,
        kind: 'datum',
        applySnapshot: applyDatumPointSnapshot,
        areSnapshotsEqual: areDatumPointSnapshotsEqual as HistoryDomainAdapter<
            DatumPointHistorySnapshot,
            DatumPointHistoryStore
        >['areSnapshotsEqual'],
    };
}

/**
 * 创建基准点历史补丁。
 *
 * @param patch 补丁字段。
 * @returns 基准点历史补丁。
 */
export function createDatumPointHistoryPatch(
    patch: Omit<EntityPatch<DatumPointHistorySnapshot>, 'adapterId' | 'entityKind'>,
): EntityPatch<DatumPointHistorySnapshot> {
    return {
        ...patch,
        before: normalizeDatumPointEntitySnapshot(patch.before),
        after: normalizeDatumPointEntitySnapshot(patch.after),
        entityKind: 'datum',
        adapterId: DATUM_POINT_HISTORY_ADAPTER_ID,
    };
}
//------ 基准点撤销重做 结束------
